<template>
  <SectionSeparatorWrapper :separator="false" headerText="My needs" headerRequiredFieldsText="indicates required field">
    <div class="form-section">
      <FormField :labelText="refData.coverReason.label" required>
        <DropdownSelect :items="coverReasons" v-model="v$.step3.coverReason.$model" />
      </FormField>
      <template v-if="isSpecificTreatment">
        <FormField v-if="isHospitalAndExtras || isHospital" :labelText="refData.hospitalNeeds.label">
          <MultiSelect
            :items="refData.hospitalNeeds.options"
            :selectedItems="step3.specificTreatmentCategory"
            placeholderText="Search for a clinical category"
            @updateData="updateTreatmentCategory"
          />
        </FormField>
        <FormField v-if="isHospitalAndExtras || isExtras" :labelText="refData.extrasNeeds.label">
          <MultiSelect
            :items="refData.extrasNeeds.options"
            :selectedItems="step3.specificTreatmentModality"
            placeholderText="Search for an extras modality"
            @updateData="updateTreatmentModality"
          />
        </FormField>
      </template>
      <FormField v-if="showPregnancyFlag">
        <input id="pregnancy" type="checkbox" value="true" v-model="v$.step3.noPregnancy.$model" name="pregnancy" ref="pregnancy" />
        <label for="pregnancy">{{ refData.pregnancy.label }}</label>
      </FormField>
    </div>
  </SectionSeparatorWrapper>
</template>

<script>
import { required, requiredIf, minLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { FormField, SectionSeparatorWrapper } from '@/components';
import { CoverComposition, CoverTypeValue, CoverReason } from '@general/js/enums';
import { MultiSelect, DropdownSelect } from '@general/js/components/';
import { convertCoverReason } from '@general/js/utils/QuoteAcceptanceUtils';
import ChangeCoverMixins from '../ChangeCoverMixins';

export default {
  name: 'MyNeeds',

  setup() {
    return { v$: useVuelidate() };
  },

  components: {
    FormField,
    MultiSelect,
    SectionSeparatorWrapper,
    DropdownSelect,
  },

  mixins: [ChangeCoverMixins],

  props: {
    // UI data for change cover
    refData: {
      type: Object,
      required: true,
    },
    prefillDetails: {
      type: Boolean,
      required: true,
    },
  },

  activated() {
    this.isActive = true;

    this.step3 = this.myNeeds;
  },

  deactivated() {
    this.isActive = false;
  },

  data() {
    return {
      step3: {
        coverReason: null,
        specificTreatmentCategory: [],
        specificTreatmentModality: [],
        noPregnancy: false,
      },
      isActive: false,
    };
  },

  watch: {
    v$: {
      handler(newData) {
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('responseUpdated', {
              response: this.step3,
              isInvalid: newData.$invalid || newData.$error,
            });
          });
        }
      },
      deep: true,
    },

    showPregnancyFlag: {
      // Reset pregnancy flag when cover reason / cover type is changed
      handler(bool) {
        if (!bool) {
          this.step3.noPregnancy = true;
        }
      },
    },
  },

  computed: {
    isHospitalAndExtras() {
      if (this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS) {
        return true;
      }
      return false;
    },

    isHospital() {
      if (this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_ONLY) {
        return true;
      }
      return false;
    },

    isExtras() {
      if (this.changeCoverData.step2.coverType === CoverTypeValue.EXTRAS_ONLY) {
        return true;
      }
      return false;
    },

    coverOptions() {
      if ([CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT].includes(this.changeCoverData.step2.composition) && this.changeCoverData.step2.familyPlus === true) {
        // if plus composition, only display 'Hospital && extras' option
        return this.refData.coverType.options.filter((option) => {
          return RegExp(/(hospital)/i).test(option.display) && RegExp(/(extras)/i).test(option.display);
        });
      }
      return this.refData.coverType.options;
    },

    isPlanningFamily() {
      if (this.step3.coverReason === convertCoverReason(CoverReason.PLANNING_A_FAMILY)) {
        return true;
      }
      return false;
    },

    isSpecificTreatment() {
      if (this.step3.coverReason === convertCoverReason(CoverReason.SPECIFIC_TREATMENT)) {
        return true;
      }
      return false;
    },

    showPregnancyFlag() {
      /* Show "Do not include pregnancy" flag under the following conditions:
      /   Hospital & Extras - hidden for planning a family and specific treatment
      /   Hospital Only -  hidden for planning a family and specific treatment
      /   Extras Only -  hidden for all the options
      */
      return !this.isSpecificTreatment && !this.isPlanningFamily && !this.isExtras && (this.isHospitalAndExtras || this.isHospital);
    },

    coverReasons() {
      return this.refData.coverReason.options.map((reason) => {
        return { name: reason.display, value: reason.returnValue };
      });
    },
  },

  methods: {
    updateTreatmentCategory(newData) {
      this.step3.specificTreatmentCategory = newData;
    },

    updateTreatmentModality(newData) {
      this.step3.specificTreatmentModality = newData;
    },
  },

  validations() {
    return {
      step3: {
        coverReason: {
          required,
        },
        noPregnancy: {},
        specificTreatmentCategory: {
          required: requiredIf(() => {
            return this.isSpecificTreatment && (this.isHospitalAndExtras || this.isHospital);
          }),
          minLength: minLength(1),
        },
        specificTreatmentModality: {
          required: requiredIf(() => {
            return this.isSpecificTreatment && (this.isHospitalAndExtras || this.isExtras);
          }),
          minLength: minLength(1),
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped></style>
